/**
 * Dữ liệu mẫu và máy chủ giả của màn Người dùng nền tảng (tenant-service,
 * `/api/v1/platform-staff`, ADR-0057).
 *
 * Đóng vai tenant-service khi chưa đăng nhập Keycloak thật hoặc khi URL có
 * `?mock=`. Máy chủ giả **từ chối y như backend** — cùng luật trạng thái
 * (`modules/nguoi-dung-nen-tang/nghiep-vu/thao-tac.ts`), cùng `error_code` — để
 * nhánh lỗi của form chạy được khi chưa có backend. Các nhánh xấu dựng sẵn:
 *
 * - `ns-002` là `PLATFORM_ADMIN` đang hoạt động duy nhất ngoài người đăng nhập,
 *   nên khoá `ns-001` được; khoá cả hai thì ca "admin cuối cùng" bật.
 * - `ns-004` còn `CHO_DUYET` do chính `mock-user` đề nghị → không tự duyệt được.
 * - `ns-005` còn `CHO_DUYET` do người khác đề nghị → duyệt được.
 * - `ns-006` đang bị khoá tạm vì nhập sai mật khẩu.
 * - `ns-007` đã duyệt mà chưa cài OTP (ADR-0007 mục 9 bắt buộc MFA).
 * - email `trung@ops.platform.test` → `409 USER_ALREADY_EXISTS`.
 * - domain `abc.test` (của tenant) → `409 TENANT_DOMAIN_CONFLICT`.
 * - domain ngoài `reserved_domains` với PLATFORM_ADMIN/AUDITOR → `422`.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây.
 */
import type { ApiError } from '../lib/errors';
import {
  doiDomainDanhRieng,
  duyetDuoc,
  khoaDuoc,
  laAdminCuoiCung,
  moKhoaDuoc,
} from '../modules/nguoi-dung-nen-tang/nghiep-vu/thao-tac.ts';
import type {
  PlatformStaff,
  PlatformStaffCreate,
  PlatformStaffPage,
  PlatformStaffStatus,
  PlatformStaffUpdate,
} from '../modules/nguoi-dung-nen-tang/types';

/** Id của người đang đăng nhập ở dữ liệu mẫu, khớp `mock/ngu-canh.ts`. */
/** Id của tài khoản mẫu đang đăng nhập; trùng `user_id` mà `nguCanhMau` trả ra. */
export const TOI = 'mock-user';

/** Domain dành riêng của nền tảng ở dev (ADR-0007 mục 8 dùng `ops.platform.test`). */
const DOMAIN_DANH_RIENG = ['ops.platform.test', 'ops.foxai.com.vn'];

/** Domain đã ánh xạ về tenant; mời nhân sự nền tảng bằng email này là sai (ADR-0057 mục 4). */
const DOMAIN_CUA_TENANT = ['abc.test', 'xyz.test'];

const EMAIL_TRUNG = 'trung@ops.platform.test';

const CAU_ADMIN_CUOI_CUNG =
  'Đây là quản trị nền tảng cuối cùng đang hoạt động. Duyệt hoặc mở một quản trị nền tảng khác trước.';

const ns = (
  id: string,
  email: string,
  ho_ten: string,
  vai_tro: PlatformStaff['vai_tro'],
  trang_thai: PlatformStaffStatus,
  thuoc: Partial<PlatformStaff> = {},
): PlatformStaff => ({
  id,
  email,
  ho_ten,
  vai_tro,
  // Mặc định chưa gán vai trò lớp 2: đúng trạng thái ngay sau khi duyệt (ADR-0074 mục 5 — quyền
  // khi đó chỉ đến từ nhóm tài khoản). Từng dòng muốn có thì truyền qua `thuoc`.
  vai_tro_nen_tang: [],
  trang_thai,
  login_locked: false,
  mfa_configured: true,
  nguoi_de_nghi: null,
  nguoi_duyet: null,
  created_at: '2026-09-01T03:00:00Z',
  updated_at: '2026-09-01T03:00:00Z',
  ...thuoc,
});

const DU_LIEU: PlatformStaff[] = [
  ns(TOI, 'admin@ops.platform.test', 'Nguyễn Vận Hành', 'PLATFORM_ADMIN', 'ACTIVE'),
  ns('ns-002', 'quantri2@ops.platform.test', 'Lê Thị Quản', 'PLATFORM_ADMIN', 'ACTIVE', {
    // Hai vai trò: cột hiện cái đầu kèm "+1", đủ để soát cách gộp
    vai_tro_nen_tang: ['HO_TRO_KHACH_HANG', 'VAN_HANH'],
  }),
  ns('ns-003', 'auditor@ops.platform.test', 'Phạm Kiểm Toán', 'AUDITOR', 'ACTIVE', {
    // Kiểm toán được gán thêm vai trò vận hành: đúng ca cộng dồn của ADR-0074 mục 5
    vai_tro_nen_tang: ['VAN_HANH'],
  }),
  ns('ns-004', 'moi1@ops.platform.test', 'Trần Chờ Duyệt', 'AUDITOR', 'CHO_DUYET', {
    nguoi_de_nghi: TOI,
    mfa_configured: false,
    created_at: '2026-10-20T08:30:00Z',
    updated_at: '2026-10-20T08:30:00Z',
  }),
  ns('ns-005', 'moi2@ops.platform.test', 'Hoàng Chờ Duyệt', 'PLATFORM_ADMIN', 'CHO_DUYET', {
    nguoi_de_nghi: 'ns-002',
    mfa_configured: false,
    created_at: '2026-10-21T02:15:00Z',
    updated_at: '2026-10-21T02:15:00Z',
  }),
  ns('ns-006', 'khoatam@ops.platform.test', 'Vũ Nhập Sai', 'AUDITOR', 'ACTIVE', {
    login_locked: true,
  }),
  ns('ns-007', 'chuacaiotp@ops.platform.test', 'Đỗ Chưa Cài', 'AUDITOR', 'ACTIVE', {
    mfa_configured: false,
    nguoi_de_nghi: 'ns-002',
    nguoi_duyet: TOI,
  }),
  ns('ns-008', 'doitac@doitac.vn', 'Bùi Đối Tác', 'VENDOR', 'ACTIVE', {
    nguoi_de_nghi: TOI,
    nguoi_duyet: 'ns-002',
  }),
  ns('ns-009', 'dakhoa@ops.platform.test', 'Mai Đã Khoá', 'AUDITOR', 'DISABLED'),
];

/** Bản sao trong phiên: thao tác ghi thấy được ngay, như API thật. */
let cacNhanSu: PlatformStaff[] = structuredClone(DU_LIEU);

const loi = (code: string, status: number, message: string, details?: unknown): ApiError => ({
  code,
  status,
  message,
  details,
});

function tim(id: string): PlatformStaff {
  const s = cacNhanSu.find((x) => x.id === id);
  if (!s) {
    throw loi('NOT_FOUND', 404, 'Không tìm thấy tài khoản này. Tải lại danh sách rồi thử lại.');
  }
  return s;
}

const nay = () => new Date().toISOString();

/**
 * Vai trò nền tảng của một nhân sự, cho `mock/vai-tro-nen-tang.ts` đọc và ghi.
 *
 * Hai máy chủ giả **dùng chung một kho**: trước đây mock vai trò giữ bảng gán riêng, nên gán
 * xong thì cột Vai trò ở bảng nhân sự vẫn trống — dữ liệu nằm ở hai chỗ không bao giờ gặp nhau.
 * Backend thật chỉ có một nguồn (`platform_user_role`), nên mock cũng phải vậy.
 */
export function vaiTroNenTangCuaNhanSu(userId: string): string[] {
  return cacNhanSu.find((x) => x.id === userId)?.vai_tro_nen_tang ?? [];
}

/** Có nhân sự này không — mock vai trò kiểm trước khi gán, như backend kiểm Keycloak. */
export function coNhanSu(userId: string): boolean {
  return cacNhanSu.some((x) => x.id === userId);
}

/** Số nhân sự đang giữ một mã vai trò — để `user_count` của màn Vai trò đếm trên cùng kho. */
export function soNhanSuGiuVaiTro(maVaiTro: string): number {
  return cacNhanSu.filter((x) => x.vai_tro_nen_tang.includes(maVaiTro)).length;
}

/** Ghi tập vai trò nền tảng; trả về tập đã ghi. */
export function datVaiTroNenTang(userId: string, maVaiTro: string[]): string[] {
  const s = tim(userId);
  s.vai_tro_nen_tang = [...maVaiTro].sort();
  s.updated_at = nay();
  return s.vai_tro_nen_tang;
}

export const mayChuNhanSuNenTang = {
  /** `GET /api/v1/platform-staff` — cursor là vị trí bắt đầu của trang sau. */
  danhSach({
    limit,
    cursor,
    trang_thai,
  }: {
    limit: number;
    cursor?: string;
    trang_thai?: PlatformStaffStatus;
  }): PlatformStaffPage {
    const loc = trang_thai ? cacNhanSu.filter((x) => x.trang_thai === trang_thai) : cacNhanSu;
    const tu = cursor ? Number(cursor) : 0;
    const items = structuredClone(loc.slice(tu, tu + limit));
    const tiep = tu + limit < loc.length ? String(tu + limit) : null;
    return { items, next_cursor: tiep };
  },

  /** `POST /api/v1/platform-staff` — bước đề nghị, chưa gán vai trò (ADR-0057 mục 3). */
  deNghi(du: PlatformStaffCreate): PlatformStaff {
    const email = du.email.trim().toLowerCase();
    const domain = email.split('@')[1] ?? '';
    if (email === EMAIL_TRUNG || cacNhanSu.some((x) => x.email === email)) {
      throw loi('USER_ALREADY_EXISTS', 409, `${email} đã có tài khoản nền tảng.`);
    }
    if (DOMAIN_CUA_TENANT.includes(domain)) {
      throw loi(
        'TENANT_DOMAIN_CONFLICT',
        409,
        `Domain ${domain} đã ánh xạ về một tenant. Tài khoản nền tảng phải dùng domain riêng.`,
      );
    }
    if (doiDomainDanhRieng(du.vai_tro) && !DOMAIN_DANH_RIENG.includes(domain)) {
      throw loi(
        'EMAIL_NOT_INVITABLE',
        422,
        `Vai trò này chỉ dùng được email thuộc ${DOMAIN_DANH_RIENG.join(', ')}.`,
      );
    }
    if (!du.ho_ten.trim()) {
      throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
        { pointer: '#/ho_ten', detail: 'Họ tên không được để trống.' },
      ]);
    }
    const moi = ns(`ns-${String(cacNhanSu.length + 1).padStart(3, '0')}`, email, du.ho_ten.trim(),
      du.vai_tro, 'CHO_DUYET', {
        nguoi_de_nghi: TOI,
        mfa_configured: false,
        created_at: nay(),
        updated_at: nay(),
      });
    cacNhanSu = [moi, ...cacNhanSu];
    return structuredClone(moi);
  },

  /** `POST /api/v1/platform-staff/{id}/approve` — người duyệt phải khác người đề nghị. */
  duyet(id: string): PlatformStaff {
    const s = tim(id);
    if (s.trang_thai !== 'CHO_DUYET') {
      throw loi('TENANT_STATUS_CONFLICT', 409, 'Tài khoản này đã được duyệt rồi.');
    }
    if (!duyetDuoc(s, TOI)) {
      throw loi(
        'PERMISSION_DENIED',
        403,
        'Người duyệt phải khác người đề nghị. Nhờ một quản trị nền tảng khác duyệt tài khoản này.',
      );
    }
    s.trang_thai = 'ACTIVE';
    s.nguoi_duyet = TOI;
    s.updated_at = nay();
    return structuredClone(s);
  },

  /** `POST /api/v1/platform-staff/{id}/resend-invite` — gửi lại email đặt mật khẩu (ADR-0060 mục 3). */
  guiLai(id: string): void {
    const s = tim(id);
    if (s.trang_thai === 'CHO_DUYET') {
      throw loi('TENANT_STATUS_CONFLICT', 409, 'Tài khoản chưa được duyệt nên chưa có gì để bàn giao.');
    }
    if (s.mfa_configured) {
      throw loi(
        'TENANT_STATUS_CONFLICT',
        409,
        'Người này đã đặt mật khẩu và cài xác thực hai lớp xong.',
      );
    }
  },

  /** `PATCH /api/v1/platform-staff/{id}` — sửa họ tên, đổi vai trò, khoá hoặc mở khoá. */
  sua(id: string, du: PlatformStaffUpdate): PlatformStaff {
    const s = tim(id);
    if (du.ho_ten !== undefined && !du.ho_ten.trim()) {
      throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
        { pointer: '#/ho_ten', detail: 'Họ tên không được để trống.' },
      ]);
    }
    if (du.vai_tro !== undefined && du.vai_tro !== s.vai_tro) {
      if (id === TOI) {
        throw loi('TENANT_STATUS_CONFLICT', 409, 'Không tự đổi vai trò của tài khoản đang đăng nhập.');
      }
      if (s.trang_thai === 'CHO_DUYET') {
        throw loi('TENANT_STATUS_CONFLICT', 409, 'Tài khoản chưa được duyệt nên chưa đổi được vai trò.');
      }
      if (du.vai_tro !== 'PLATFORM_ADMIN' && laAdminCuoiCung(s, cacNhanSu)) {
        throw loi('TENANT_STATUS_CONFLICT', 409, CAU_ADMIN_CUOI_CUNG);
      }
    }
    if (du.trang_thai === 'DISABLED' && s.trang_thai !== 'DISABLED') {
      if (id === TOI) {
        throw loi('TENANT_STATUS_CONFLICT', 409, 'Không tự khoá tài khoản đang đăng nhập.');
      }
      if (!khoaDuoc(s, TOI)) {
        throw loi('TENANT_STATUS_CONFLICT', 409, 'Tài khoản này chưa duyệt nên không khoá được.');
      }
      if (laAdminCuoiCung(s, cacNhanSu)) {
        throw loi('TENANT_STATUS_CONFLICT', 409, CAU_ADMIN_CUOI_CUNG);
      }
    }
    if (du.trang_thai === 'ACTIVE' && s.trang_thai === 'DISABLED' && !moKhoaDuoc(s)) {
      throw loi('TENANT_STATUS_CONFLICT', 409, 'Tài khoản này không ở trạng thái khoá.');
    }

    if (du.ho_ten !== undefined) s.ho_ten = du.ho_ten.trim();
    if (du.vai_tro !== undefined) s.vai_tro = du.vai_tro;
    if (du.trang_thai === 'DISABLED') s.trang_thai = 'DISABLED';
    if (du.trang_thai === 'ACTIVE' && s.trang_thai === 'DISABLED') {
      s.trang_thai = 'ACTIVE';
      s.login_locked = false; // mở khoá là xoá cả dấu vết khoá (ADR-0053)
    }
    s.updated_at = nay();
    return structuredClone(s);
  },
};
